import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  Grid,
  Paper,
  Avatar,
  LinearProgress,
  Container,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { MathJaxContext, MathJax } from "better-react-mathjax";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ScoreIcon from "@mui/icons-material/EmojiEvents";
import PercentIcon from "@mui/icons-material/PieChart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DynamicMathChart from "./DynamicMathChart";
import AIDialog from "./AIDialog";
import Logger from "./Logger";

const TestResultComponent = ({
  results,
  hpTest,
  timeLeftRef,
  handleBackToSelector,
  styles,
  theme,
  user,
}) => {
  const [openAIDialogs, setOpenAIDialogs] = useState({});
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const correctAnswers = results.filter((result) => result.isCorrect).length;
  const totalQuestions = results.length;
  const percentageCorrect = (correctAnswers / totalQuestions) * 100;
  const usedTimeInSeconds = 55 * 60 - timeLeftRef.current;
  const usedTimeMinutes = Math.floor(usedTimeInSeconds / 60);
  const usedTimeSeconds = (usedTimeInSeconds % 60).toString().padStart(2, "0");

  const handleOpenAIDialog = (index) => {
    setOpenAIDialogs((prev) => ({ ...prev, [index]: true }));
  };

  const handleCloseAIDialog = (index) => {
    setOpenAIDialogs((prev) => ({ ...prev, [index]: false }));
  };

  // Group questions by type for a better overview
  const questionsByType = results.reduce((acc, result) => {
    if (!acc[result.questionType]) {
      acc[result.questionType] = [];
    }
    acc[result.questionType].push(result);
    return acc;
  }, {});

  // Calculate stats by question type
  const typeStats = Object.entries(questionsByType).map(([type, questions]) => {
    const correctCount = questions.filter((q) => q.isCorrect).length;
    const percentage = (correctCount / questions.length) * 100;
    return {
      type,
      correct: correctCount,
      total: questions.length,
      percentage,
    };
  });

  // Debug logging
  Logger.log("Current timeLeft:", timeLeftRef.current);
  Logger.log("Total test time in seconds:", 55 * 60);
  Logger.log("Calculated used time:", usedTimeInSeconds);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <IconButton
          onClick={handleBackToSelector}
          sx={{
            mr: 2,
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.2rem", md: "2rem" },
          }}
        >
          Resultatöversikt
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Paper
        elevation={0}
        sx={{
          mb: 5,
          p: 3,
          borderRadius: "16px",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #192A3A 0%, #243447 100%)"
              : "linear-gradient(145deg, #FFFFFF 0%, #F5F8FF 100%)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "#3949AB" : "#E3F2FD",
                  width: 56,
                  height: 56,

                  mr: 2,
                }}
              >
                <ScoreIcon
                  color={theme.palette.mode === "dark" ? "inherit" : "primary"}
                />
              </Avatar>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Korrekta svar
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  {correctAnswers} / {totalQuestions}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "#00897B" : "#E0F2F1",
                  width: 56,
                  height: 56,

                  mr: 2,
                }}
              >
                <PercentIcon
                  color={theme.palette.mode === "dark" ? "inherit" : "success"}
                />
              </Avatar>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Precision
                </Typography>
                <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1.75rem", md: "2rem" },
                    }}
                  >
                    {percentageCorrect.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percentageCorrect}
                  sx={{
                    mt: 1,
                    height: 8,
                    borderRadius: 4,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.05)",
                  }}
                  color="success"
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "#F57C00" : "#FFF3E0",
                  width: 56,
                  height: 56,

                  mr: 2,
                }}
              >
                <AccessTimeIcon
                  color={theme.palette.mode === "dark" ? "inherit" : "warning"}
                />
              </Avatar>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Tid använd
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  {usedTimeMinutes}:{usedTimeSeconds}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Type breakdown section */}
      <Paper
        elevation={0}
        sx={{
          mb: 5,
          p: 3,
          borderRadius: "16px",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #192A3A 0%, #243447 100%)"
              : "linear-gradient(145deg, #FFFFFF 0%, #F5F8FF 100%)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            fontWeight: 600,
            fontSize: { xs: "1.25rem", md: "1.5rem" },
          }}
        >
          Resultat per frågekategori
        </Typography>

        <Grid container spacing={2}>
          {typeStats.map((stat) => (
            <Grid item xs={12} sm={6} md={4} key={stat.type}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.02)",
                  border:
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid rgba(0,0,0,0.05)",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {stat.type}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    my: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {stat.correct} av {stat.total} rätt
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      stat.percentage > 80
                        ? "success.main"
                        : stat.percentage > 50
                          ? "info.main"
                          : "error.main"
                    }
                  >
                    {stat.percentage.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stat.percentage}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.05)",
                  }}
                  color={
                    stat.percentage > 80
                      ? "success"
                      : stat.percentage > 50
                        ? "info"
                        : "error"
                  }
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: 600,
          fontSize: { xs: "1.25rem", md: "1.5rem" },
          display: "flex",
          alignItems: "center",
        }}
      >
        Detaljerade resultat
        <Tooltip title="Klicka på 'Fråga AI' för att få en förklaring för varje fråga">
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>

      <MathJaxContext>
        {results.map((result, index) => {
          // Find the correct section
          const section = hpTest.find(
            (s) => s.questionType === result.questionType,
          );

          // Find the correct question, handling potential nested structure
          let question;
          if (section) {
            if (Array.isArray(section.questions)) {
              // För ORD-frågor
              question = section.questions.find(
                (q) => q.question === result.question,
              );
            } else if (section.questions && Array.isArray(section.questions)) {
              // För LÄS-frågor
              question = section.questions[0];
            }
          }

          if (!section || !question) {
            console.error(
              "Could not find matching section or question for result:",
              result,
            );
            return null;
          }

          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                border: result.isCorrect
                  ? `1px solid ${theme.palette.mode === "dark" ? "rgba(76, 175, 80, 0.3)" : "rgba(76, 175, 80, 0.2)"}`
                  : `1px solid ${theme.palette.mode === "dark" ? "rgba(244, 67, 54, 0.3)" : "rgba(244, 67, 54, 0.2)"}`,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  height: 6,
                  width: "100%",
                  bgcolor: result.isCorrect ? "success.main" : "error.main",
                }}
              />

              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={9}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Chip
                        label={result.questionType}
                        size="small"
                        sx={{
                          borderRadius: "8px",
                          fontWeight: 600,
                          mr: 1,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(0,0,0,0.06)",
                        }}
                      />
                      <Chip
                        icon={
                          result.isCorrect ? (
                            <CheckCircleIcon fontSize="small" />
                          ) : (
                            <CancelIcon fontSize="small" />
                          )
                        }
                        label={result.isCorrect ? "Rätt" : "Fel"}
                        size="small"
                        color={result.isCorrect ? "success" : "error"}
                        sx={{
                          borderRadius: "8px",
                          fontWeight: 500,
                        }}
                      />
                    </Box>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 4,
                        mt: 2,
                        fontWeight: 600,
                        fontSize: "1.3rem",
                        lineHeight: 1.4,
                      }}
                    >
                      <MathJax>{result.question}</MathJax>
                    </Typography>

                    {result.questionType === "XYZ" ? (
                      <></>
                    ) : result.questionType === "DTK" ? (
                      <Box
                        sx={{
                          mb: 3,
                          mt: 2,
                          p: 2,
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.02)",
                          borderRadius: 2,
                        }}
                      >
                        <DynamicMathChart
                          chartType={question.chartType}
                          chartData={question.chartData}
                          chartConfig={question.chartConfig}
                        />
                      </Box>
                    ) : null}

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Ditt svar:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: result.isCorrect
                              ? "success.main"
                              : "error.main",
                          }}
                        >
                          <MathJax>{result.selectedOption}</MathJax>
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Korrekt svar:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: "success.main",
                          }}
                        >
                          <MathJax>{result.correctAnswer}</MathJax>
                        </Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Svarsalternativ:
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {question.options.map((option, optionIndex) => (
                        <Chip
                          key={optionIndex}
                          label={option}
                          color={
                            option === result.correctAnswer
                              ? "success"
                              : option === result.selectedOption &&
                                  !result.isCorrect
                                ? "error"
                                : "default"
                          }
                          variant={
                            option === result.selectedOption ||
                            option === result.correctAnswer
                              ? "filled"
                              : "outlined"
                          }
                          sx={{
                            borderRadius: "8px",
                            px: 0.5,
                            "& .MuiChip-label": {
                              px: 1,
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={3}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: isMobile ? "flex-start" : "center",
                    }}
                  >
                    <Button
                      variant="purp"
                      onClick={() => handleOpenAIDialog(index)}
                      startIcon={<GenerateIcon />}
                      sx={{}}
                    >
                      Fråga AI
                    </Button>
                  </Grid>
                </Grid>

                <AIDialog
                  userId={user.uid}
                  open={openAIDialogs[index] || false}
                  onClose={() => handleCloseAIDialog(index)}
                  initialQuestion={`Fråga: ${result.question}
${result.questionType === "XYZ" && question.equation ? `Ekvation: ${question.equation}` : ""}
Alternativ: ${question.options.join(", ")}
Korrekt svar: ${result.correctAnswer}
Valt svar: ${result.selectedOption}
Förklara denna fråga och hjälp mig förstå svarsalternativen. Skriv på svenska.`}
                  systemMessage="Du är en hjälpsam AI-assistent designad för att förklara och hjälpa till med högskoleprovsfrågor. Ge en kort och enkel förklaring. Skriv på svenska."
                  theme={theme}
                />
              </CardContent>
            </Paper>
          );
        })}
      </MathJaxContext>

      <Box sx={{ textAlign: "center", mt: 5, mb: 3 }}>
        <Button
          variant="print"
          onClick={handleBackToSelector}
          startIcon={<ArrowBackIcon />}
          sx={{}}
        >
          Tillbaka till översikten
        </Button>
      </Box>
    </Container>
  );
};

export default TestResultComponent;
